import React, { useCallback, memo } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { getBooleanFF } from '@atlaskit/platform-feature-flags';
import { useOgTenantInfo } from '@atlassian/jira-eoc-tenant-info/src/controllers/index.tsx';
import HelpPanelLink from '@atlassian/jira-help-panel-button';
import Graph from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/other/components/graph';
import JsmAlerts from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/software/components/jsm-alerts';
import JsmOncall from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/software/components/jsm-oncall';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import { useOpsgenieBaseUrl } from '@atlassian/jira-platform-get-og-base-url/src/main.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useProjectContext } from '@atlassian/jira-providers-project-context';
import {
	ALERTS_FEATURE,
	ONCALL_FEATURE,
	SERVICES_FEATURE,
	ENABLED,
} from '@atlassian/jira-servicedesk-common/src/service-project-features/constants.tsx';
import { APP_NAMES, toPackageName } from '@atlassian/jira-servicedesk-common/src/utils/app-names';
import { ExternalLink } from '../../../../../common/ui/external-link';
import { FeatureToggleTile } from '../../../../../common/ui/feature-toggle-tile/index.tsx';
import useSaveFailedFlag from '../../../../../controllers/common/save-failed-flag/index.tsx';
import useServiceProjectFeaturesController from '../../../../../controllers/common/service-project-features-controller/index.tsx';
import { FEATURE_TITLE_LOOKUP } from './constants';
import messages from './messages';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DevOpsFeatures = memo<Record<any, any>>(() => {
	const { formatMessage } = useIntl();
	const [{ opsgenieBaseUrl }] = useOpsgenieBaseUrl(toPackageName(APP_NAMES.ITSM_FEATURES_SETTINGS));
	const { data, saving, update } = useServiceProjectFeaturesController();
	const showSaveErrorFlag = useSaveFailedFlag();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { data: projectContext } = useProjectContext();
	const { isConsolidated } = useOgTenantInfo();
	const isClassicProject = Boolean(projectContext && !projectContext.simplified);

	const handleOnChange = useCallback(
		(feature: string, enabled: boolean, analyticEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticEvent, 'settingsToggle', { field: feature });
			update({
				feature,
				enabled,
				onFail: () => {
					showSaveErrorFlag(formatMessage(FEATURE_TITLE_LOOKUP[feature]), enabled);
				},
			});
		},
		[formatMessage, showSaveErrorFlag, update],
	);

	const createLearnMoreLink = (
		href: string,
		message: string,
		articleId: string,
		eventName: string,
	) => {
		const learnMoreLinkButton = <ExternalLink href={href}>{message}</ExternalLink>;
		return (
			<HelpPanelLink
				articleId={articleId}
				appearance="link"
				spacing="none"
				fallbackComponent={learnMoreLinkButton}
				onClick={() =>
					fireUIAnalytics(createAnalyticsEvent({}), 'helpLink clicked', eventName, {
						articleId,
					})
				}
			>
				{message}
			</HelpPanelLink>
		);
	};

	const classicLearnMoreAboutAlertArticleId = '1s3I1JN4Y9Sejgt7YDLK7J';
	const nextGenLearnMoreAboutAlertArticleId = 'ZOHywNcUfDBlV4uvkv0Po';
	const learnMoreAboutAlerts = createLearnMoreLink(
		'https://docs.opsgenie.com/docs/new-user-alert-management',
		formatMessage(messages.alertsLink),
		isClassicProject ? classicLearnMoreAboutAlertArticleId : nextGenLearnMoreAboutAlertArticleId,
		'learnMoreAboutAlerts',
	);

	const learnMoreAboutAlertsConsolidated = createLearnMoreLink(
		'https://support.atlassian.com/jira-service-management-cloud/docs/what-are-alerts',
		formatMessage(messages.alertsLinkConsolidated),
		'8nyr2PWtUtH4wbYm8JAV2',
		'learnMoreAboutAlertsConsolidated',
	);

	const learnMoreAboutOnCall = createLearnMoreLink(
		'https://docs.opsgenie.com/docs/new-user-on-call-schedules',
		formatMessage(messages.oncallLink),
		'2zyL8oLj6I4a56rpl8TPZ3',
		'learnMoreAboutOnCall',
	);
	const learnMoreAboutOnCallConsolidated = createLearnMoreLink(
		'https://support.atlassian.com/jira-service-management-cloud/docs/how-do-on-call-schedules-work-in-operations',
		formatMessage(messages.oncallLinkConsolidated),
		'7KkB6nTIcLcVRC2T39y2xI',
		'learnMoreAboutOnCallConsolidated',
	);

	const learnMoreAboutServices = createLearnMoreLink(
		'https://support.atlassian.com/jira-service-desk-cloud/docs/manage-your-service-registry',
		formatMessage(messages.servicesLink),
		'3osolPEuol1YCaO4FthXUl',
		'learnMoreAboutService',
	);

	return (
		<>
			{isClassicProject && (
				<FeatureToggleTile
					feature={SERVICES_FEATURE}
					illustration={<Graph alt="" />}
					title={formatMessage(messages.servicesTitle)}
					value={
						// @ts-expect-error TS2532 - Object is possibly 'undefined'.
						data?.[SERVICES_FEATURE].status === ENABLED
					}
					saving={saving[SERVICES_FEATURE]}
					onChange={handleOnChange}
				>
					<FormattedMessage
						{...messages.servicesDescription}
						values={{
							learnMoreLink: learnMoreAboutServices,
						}}
					/>
				</FeatureToggleTile>
			)}
			<FeatureToggleTile
				feature={ALERTS_FEATURE}
				title={formatMessage(messages.alertsTitle)}
				illustration={<JsmAlerts alt="" />}
				value={
					// @ts-expect-error TS2532 - Object is possibly 'undefined'.
					data?.[ALERTS_FEATURE].status === ENABLED
				}
				saving={saving[ALERTS_FEATURE]}
				onChange={handleOnChange}
			>
				{isConsolidated ? (
					<FormattedMessage
						{...messages.alertsConsolidatedDescription}
						values={{
							learnMoreLink: learnMoreAboutAlertsConsolidated,
						}}
					/>
				) : (
					<FormattedMessage
						{...messages.alertsDescription}
						values={{
							opsgenieLink: (
								<ExternalLink
									href={`${String(opsgenieBaseUrl)}/alert`}
									enabled={Boolean(opsgenieBaseUrl)}
								>
									{formatMessage(messages.alertsOgLink)}
								</ExternalLink>
							),
							learnMoreLink: learnMoreAboutAlerts,
						}}
					/>
				)}
			</FeatureToggleTile>
			<FeatureToggleTile
				feature={ONCALL_FEATURE}
				illustration={<JsmOncall alt="" />}
				title={formatMessage(messages.oncallTitle)}
				value={
					// @ts-expect-error TS2532 - Object is possibly 'undefined'.
					data?.[ONCALL_FEATURE].status === ENABLED
				}
				saving={saving[ONCALL_FEATURE]}
				onChange={handleOnChange}
			>
				{isConsolidated &&
				getBooleanFF('platform.jira.frontend.remove-the-linkage-to-opsgenie-in-jsm') ? (
					<FormattedMessage
						{...messages.oncallConsolidatedDescription}
						values={{
							learnMoreLink: learnMoreAboutOnCallConsolidated,
						}}
					/>
				) : (
					<FormattedMessage
						{...messages.oncallDescription}
						values={{
							opsgenieLink: (
								<ExternalLink
									href={`${String(opsgenieBaseUrl)}/schedule/whoIsOnCall`}
									enabled={Boolean(opsgenieBaseUrl)}
								>
									{formatMessage(messages.oncallOgLink)}
								</ExternalLink>
							),
							learnMoreLink: learnMoreAboutOnCall,
						}}
					/>
				)}
			</FeatureToggleTile>
		</>
	);
});

export default DevOpsFeatures;
